import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '@env/environment';
import { AuthStateManager } from '@features/auth/services/auth-state-manager-service';
import { HighlightProductResponse } from '@api/models';
import { PaginatedResponse } from '@core/models/pagination.model';

import {
  MenuResponse,
  MenuCategoryResponse,
  MenuProductResponse,
} from '../models/response/menu-response.model';

interface VenueCachePayload<T> {
  value: T;
  expiresAt: number;
  venueId: string | null;
}

@Injectable({ providedIn: 'root' })
export class MenuService {
  private http = inject(HttpClient);
  private authState = inject(AuthStateManager);

  private readonly CACHE_KEY = 'dinno-menu-cache-v1';
  private readonly VENUE_KEY = 'dinno-menu-venue-id';
  private readonly CACHE_TTL = 10 * 60 * 1000;
  private readonly TOP_CACHE_KEY = 'dinno-top-selling-v1';
  private readonly TOP_CACHE_TTL = 2 * 60 * 1000;
  private readonly REC_CACHE_KEY = 'dinno-recommended-v1';
  private readonly REC_CACHE_TTL = 2 * 60 * 1000;

  private saveVenueCache<T>(key: string, value: T, ttlMs: number) {
    const payload: VenueCachePayload<T> = {
      value,
      expiresAt: Date.now() + ttlMs,
      venueId: this.authState.foodVenueId(),
    };
    localStorage.setItem(key, JSON.stringify(payload));
  }

  private loadVenueCache<T>(key: string): T | null {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as VenueCachePayload<T>;
      if (parsed.venueId && parsed.venueId !== this.authState.foodVenueId()) return null;
      if (Date.now() > parsed.expiresAt) return null;
      return parsed.value;
    } catch {
      return null;
    }
  }

  clearCache() {
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.VENUE_KEY);
    localStorage.removeItem(this.TOP_CACHE_KEY);
    localStorage.removeItem(this.REC_CACHE_KEY);
  }

  getMenu(): Observable<MenuResponse> {
    const currentVenueId = this.authState.foodVenueId();
    const cachedVenueId = localStorage.getItem(this.VENUE_KEY);

    if (currentVenueId && cachedVenueId && currentVenueId !== cachedVenueId) {
      this.clearCache();
    }

    const raw = localStorage.getItem(this.CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Date.now() < parsed.expiresAt) return of(parsed.value as MenuResponse);
    }

    return this.http.get<MenuResponse>(`${environment.apiUrl}/menus`).pipe(
      tap((data) => {
        const payload = { value: data, expiresAt: Date.now() + this.CACHE_TTL };
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(payload));
        if (currentVenueId) localStorage.setItem(this.VENUE_KEY, currentVenueId);
      }),
    );
  }

  getRecommended(limit = 20): Observable<HighlightProductResponse[]> {
    const cached = this.loadVenueCache<HighlightProductResponse[]>(this.REC_CACHE_KEY);
    if (cached) return of(cached);

    return this.http
      .get<
        PaginatedResponse<HighlightProductResponse>
      >(`${environment.apiUrl}/products/featured/recommended`, { params: { limit: String(limit) } })
      .pipe(
        map((page) => page.content),
        tap((items) => this.saveVenueCache(this.REC_CACHE_KEY, items, this.REC_CACHE_TTL)),
        catchError(() => of([])),
      );
  }

  getTopSelling(limit = 5, days = 30): Observable<HighlightProductResponse[]> {
    const cached = this.loadVenueCache<HighlightProductResponse[]>(this.TOP_CACHE_KEY);
    if (cached) return of(cached);

    return this.http
      .get<PaginatedResponse<HighlightProductResponse>>(
        `${environment.apiUrl}/products/top-selling`,
        {
          params: { limit: String(limit), days: String(days) },
        },
      )
      .pipe(
        map((page) => page.content),
        tap((items) => this.saveVenueCache(this.TOP_CACHE_KEY, items, this.TOP_CACHE_TTL)),
        catchError(() => of([])),
      );
  }

  getProductByPublicId(publicId: string): Observable<MenuProductResponse | undefined> {
    return this.getMenu().pipe(
      map((res) => {
        const all = this.collectAllProducts(res.menu ?? []);
        return all.find((p) => p.publicId === publicId);
      }),
      catchError(() => of(undefined)),
    );
  }

  private collectAllProducts(categories: MenuCategoryResponse[]): MenuProductResponse[] {
    const all: MenuProductResponse[] = [];
    const scan = (cats: MenuCategoryResponse[]) => {
      for (const c of cats) {
        if (c.products) all.push(...c.products);
        if (c.subcategory) scan(c.subcategory);
      }
    };
    scan(categories);
    return all;
  }
}
